import { Component } from '@angular/core';
import { Gender, User } from '../../model/user';
import { LocalStorageService } from '../../services/local-storage.service';
import { ActivatedRoute } from '@angular/router';
import { FriendProfile, Friendship, FriendshipStatus } from '../../model/friends';
import { CourtReservation } from '../../model/court-reservation';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PlayerSearch } from '../../model/player-search';
import { RouterService } from '../../services/router.service';
import { FriendsService } from '../../services/friends.service';

@Component({
  selector: 'app-invite-players',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './invite-players.component.html',
  styleUrl: './invite-players.component.css'
})
export class InvitePlayersComponent {

  friendships: boolean = false;
  inviteFriends: boolean = true;
  friends: FriendProfile[] = [];
  potentialFriends: FriendProfile[] = [];
  idx: number = -1;
  courtReservation: CourtReservation;
  isSingle: boolean = false;
  gender: boolean = false;
  selectedLeague: string = '0';
  currentUser: User;
  private currentUserId = 0;
  private pendingRequestsMap = new Map<number, Friendship>();

  constructor(
    private route: ActivatedRoute,
    private localStorageService: LocalStorageService,
    private routerService: RouterService,
    private location: Location,
    private friendsService: FriendsService
  ) {
    this.courtReservation =
      (this.safeGetItem<CourtReservation>('courtReservation') as CourtReservation) ||
      ({ player_ids: [-1, -1, -1, -1] } as CourtReservation);
    this.currentUser =
      (this.safeGetItem<User>('currentUser') as User) ||
      ({ id: 0, username: '', name: '', last_name: '', pp_points: 0 } as unknown as User);
    this.currentUserId = this.normalizeId(this.currentUser?.id);
    this.currentUser = { ...this.currentUser, id: this.currentUserId } as User;
  }

  ngOnInit() {
    this.isSingle = Array.isArray(this.courtReservation.player_ids)
      ? this.courtReservation.player_ids.length == 2
      : false;

    if (this.location.path().includes('friendships')) {
      this.friendships = true;
    }

    this.route.paramMap.subscribe(params => {
      const idxParam = params.get('idx');
      this.idx = idxParam ? Number(idxParam) : this.idx;
    });

    if (this.currentUserId) {
      this.loadFriendships();
    }
  }

  private loadFriendships(): void {
    this.friendsService.getFriendshipsForUser(this.currentUserId).subscribe({
      next: (friendships: Friendship[]) => {
        this.processFriendships(friendships || []);
      },
      error: (error) => {
        console.error('Failed to load friendships', error);
        this.friends = [];
        this.potentialFriends = [];
        this.pendingRequestsMap.clear();
      },
    });
  }

  private processFriendships(friendships: Friendship[]): void {
    this.friends = [];
    this.potentialFriends = [];
    this.pendingRequestsMap.clear();

    for (const friendship of friendships) {
      if (!friendship) {
        continue;
      }

      if (friendship.status === FriendshipStatus.Approved) {
        const profile = this.extractCounterpart(friendship);
        if (profile) {
          this.friends.push(profile);
        }
        continue;
      }

      if (
        friendship.status === FriendshipStatus.Pending &&
        friendship.friend_id === this.currentUserId
      ) {
        const profile = this.toProfile(friendship.user);
        if (profile) {
          this.potentialFriends.push(profile);
          this.pendingRequestsMap.set(profile.id, friendship);
        }
      }
    }
  }

  private extractCounterpart(friendship: Friendship): FriendProfile | null {
    if (!this.currentUserId) {
      return null;
    }

    if (friendship.user_id === this.currentUserId) {
      return this.toProfile(friendship.friend);
    }

    return this.toProfile(friendship.user);
  }

  private toProfile(data?: FriendProfile | null): FriendProfile | null {
    if (!data || typeof data.id !== 'number') {
      return null;
    }

    return {
      id: data.id,
      username: data.username,
      name: data.name,
      last_name: data.last_name,
      pp_points: data.pp_points,
      profile_image: data.profile_image,
    };
  }

  private safeGetItem<T>(key: string): T | null {
    try {
      return this.localStorageService.getItem(key) as T;
    } catch (error) {
      return null;
    }
  }

  private normalizeId(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  invite(i: number) {
    for (let j = 0; j < this.courtReservation.player_ids.length; j++) {
      if (this.courtReservation.player_ids[j] == i) {
        this.courtReservation.player_ids[j] = -1;
        if (j < this.idx || this.idx == -1) {
          this.idx = j;
        }
        this.localStorageService.setItem('courtReservation', this.courtReservation);
        return;
      }
    }

    if (this.idx < this.courtReservation.player_ids.length && this.idx != -1) {
      this.courtReservation.player_ids[this.idx] = i;
      this.localStorageService.setItem('courtReservation', this.courtReservation);
      if (
        this.idx + 1 < this.courtReservation.player_ids.length &&
        this.courtReservation.player_ids[this.idx + 1] == -1
      ) {
        this.idx = this.idx + 1;
      } else {
        this.idx = -1;
      }
    }
  }

  invited(i: number): boolean {
    for (let j = 0; j < this.courtReservation.player_ids.length; j++) {
      if (this.courtReservation.player_ids[j] == i) {
        return true;
      }
    }
    return false;
  }

  changeMatch(type: string) {
    if (type == 'single') {
      this.isSingle = true;
      if (this.courtReservation.player_ids.length != 2) {
        this.courtReservation.player_ids = [
          this.courtReservation.player_ids[0],
          this.courtReservation.player_ids[1],
        ];
      }
    } else {
      this.isSingle = false;
      if (this.courtReservation.player_ids.length != 4) {
        this.courtReservation.player_ids = [
          this.courtReservation.player_ids[0],
          this.courtReservation.player_ids[1],
          -1,
          -1,
        ];
      }
    }

    this.localStorageService.setItem('courtReservation', this.courtReservation);
  }

  searchPlayers() {
    this.localStorageService.setItem(
      'playerSearch',
      new PlayerSearch(Number(this.selectedLeague), this.gender ? Gender.Female : Gender.Male)
    );
    this.routerService.navigateTo('search-players/' + this.idx);
  }

  accept(id: number) {
    const friendship = this.pendingRequestsMap.get(id);
    if (!friendship) {
      return;
    }

    const payload: Friendship = {
      ...friendship,
      status: FriendshipStatus.Approved,
    };

    this.friendsService.updateFriendship(payload).subscribe({
      next: () => {
        this.loadFriendships();
      },
      error: (error) => {
        console.error('Failed to approve friend request', error);
      },
    });
  }
}
